import { Dispatch } from "redux";

import { loadAndTrack } from "redux-request-loading";

import { Payload } from "@neworbit/redux-helpers";

import { toast } from "@teamprevent/common/toasts";
import {
    AppState,
    OrgStructureCreateModel,
    OrgStructureDetailModel,
    OrgStructureEditModel
} from "@teamprevent/common/orgStructure/models";

import * as actions from "./actiontypes";
import { orgStructureApi } from "./orgStructureApi";

import { push } from "redux-little-router";

export type OrgStructureAction =
      ({ type: typeof actions.LOAD_ORGSTRUCTURES_SUCCESS } & Payload<OrgStructureDetailModel[]>)
    | ({ type: typeof actions.CREATE_ORGSTRUCTURE_SUCCESS } & Payload<OrgStructureDetailModel>)
    | ({ type: typeof actions.LOAD_ORGSTRUCTURE_DETAIL_SUCCESS } & Payload<OrgStructureDetailModel>)
    | ({ type: typeof actions.SAVE_ORGSTRUCTURE_SUCCESS } & Payload<OrgStructureDetailModel>)
    | ({ type: typeof actions.SET_CURRENT_ORGSTRUCTURE } & Payload<OrgStructureDetailModel>)
    | ({ type: typeof actions.DELETE_ORGSTRUCTURE_SUCCESS } & Payload<any>)
    | ({ type: typeof actions.CLEAR_ORGSTRUCTURES });

export const setCurrentOrgStructureSuccess = (payload: OrgStructureDetailModel): OrgStructureAction => ({
    payload,
    type: actions.SET_CURRENT_ORGSTRUCTURE
});

export const loadOrgStructuresSuccess = (payload: OrgStructureDetailModel[]): OrgStructureAction => ({
    payload,
    type: actions.LOAD_ORGSTRUCTURES_SUCCESS
});

export const createOrgStructureSuccess = (payload: OrgStructureDetailModel): OrgStructureAction => ({
    payload,
    type: actions.CREATE_ORGSTRUCTURE_SUCCESS
});

export const saveOrgStructureSuccess = (payload: OrgStructureDetailModel): OrgStructureAction => ({
    payload,
    type: actions.SAVE_ORGSTRUCTURE_SUCCESS
});

export const deleteOrgStructureSuccess = (id: any): OrgStructureAction => ({
    payload: id,
    type: actions.DELETE_ORGSTRUCTURE_SUCCESS
});

export function setCurrentOrgStructure(orgStructure: OrgStructureDetailModel) {
    return async (dispatch: Dispatch) => {
        dispatch(setCurrentOrgStructureSuccess(orgStructure));
    };
}

export function createOrgStructure(customerId: string, orgStructure: OrgStructureCreateModel, path: string) {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actions.CREATE_ORGSTRUCTURE, orgStructureApi.create(customerId, orgStructure));
        dispatch(createOrgStructureSuccess(result));
        dispatch(push(`${path}`, {}));
    };
}

export function saveOrgStructure(customerId: string, orgStructure: OrgStructureEditModel, path?: string) {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actions.SAVE_ORGSTRUCTURE, orgStructureApi.save(customerId, orgStructure));
        dispatch(saveOrgStructureSuccess(result));
        if (path !== undefined) {
            dispatch(push(`${path}`, {}));
        }
    };
}

export function deleteOrgStructure(customerId: string, id: string, path: string) {
    return async (dispatch: Dispatch) => {
        const response = await loadAndTrack(dispatch, "deleteOrgStructure", orgStructureApi.delete(customerId, id));
        if (!response.success) {
            toast.warning(response.error);
            return;
        }
        dispatch(deleteOrgStructureSuccess(id));
        toast.success("Organisation structure level deleted successfully.");
        dispatch(push(`${path}`, {}));
    };
}

export function loadOrgStructures({ customerId, parentOrgStructureId, orgStructureOneId, orgStructureTwoId, orgStructureThreeId, orgStructureFourId, name }:
    { customerId: string; parentOrgStructureId?: string; orgStructureOneId?: string; orgStructureTwoId?: string; orgStructureThreeId?: string;
        orgStructureFourId?: string; name?: string; }) {
    return async (dispatch: Dispatch) => {
        const orgStructureId = parentOrgStructureId || orgStructureFourId || orgStructureThreeId || orgStructureTwoId || orgStructureOneId;
        const result = await loadAndTrack(dispatch, "loadOrgStructures", orgStructureApi.getAll(customerId, orgStructureId, name));
        dispatch(loadOrgStructuresSuccess(result));
        return result;
    };
}

export const loadOrgStructureDetailSuccess = (orgStructure: OrgStructureDetailModel) => ({
    payload: orgStructure,
    type: actions.LOAD_ORGSTRUCTURE_DETAIL_SUCCESS
});

export function loadOrgStructureDetail({ customerId, orgStructureOneId, orgStructureTwoId, orgStructureThreeId, orgStructureFourId }:
    {
        orgStructureOneId: string;
        orgStructureTwoId: string;
        orgStructureThreeId: string;
        orgStructureFourId: string;
        customerId: string;
    }) {
    return async (dispatch: Dispatch, getState: () => AppState) => {
        const orgStructureId = orgStructureFourId || orgStructureThreeId || orgStructureTwoId || orgStructureOneId;
        let orgStructure = getState().orgStructures.filter(c => c.id === orgStructureId)[0];

        if (orgStructure === undefined) {
            orgStructure = await loadAndTrack(dispatch, "loadOrgStructureDetail", orgStructureApi.detail(customerId, orgStructureId));
            dispatch(loadOrgStructureDetailSuccess(orgStructure));
        }

        const setCurrentOrgStructureAction = setCurrentOrgStructureSuccess;
        dispatch(setCurrentOrgStructureAction(orgStructure));
    };
}

export const clearAllOrgStructures = (): OrgStructureAction => ({
    type: actions.CLEAR_ORGSTRUCTURES
});

export function clearOrgStructures() {
    return async (dispatch: Dispatch) => {
        dispatch(clearAllOrgStructures());
    };
}
